<script setup lang="ts">
import {
  AlarmIcon,
  BriefcaseIcon,
  BuildingSkyscraperIcon,
  CodeIcon, DatabasePlusIcon,
  DeviceDesktopCodeIcon,
  GraphIcon,
  KeyIcon,
  PhoneCallIcon,
  Server2Icon,
  TargetArrowIcon,
  TopologyRing2Icon,
  TopologyStar3Icon,
  WorldIcon
} from "vue-tabler-icons";
import {LogoServer} from "#components";

const props = defineProps<{hideCtas?: boolean}>()

const {isEnterprise} = useEnvironment()
const {plan, loading} = useSubscriptionForEnvironment()

const plans = computed(() => {
  return [
    {
      planIcon: DeviceDesktopCodeIcon,
      planName: "Developer",
      planSubtitle: "Everything you need to start with Axon",
      pricing: "Completely free",
      active: !loading.value && !props.hideCtas && !isEnterprise.value && !plan?.value,
      connections: "Up to 10",
      cloudConnection: "Required",
      metricRetention: "30 days",
      buttonUrl: "?plan-dialog=developer",
      buttonText: plan?.value ? 'Downgrade' : 'Included',
      buttonColor: 'primary',
      buttonDisabled: !plan?.value,
      buttonHidden: isEnterprise.value,
      advantages: [
        {
          text: "Get started with Axon Server",
          subtext: 'Try out everything Axon Server has to offer',
          icon: LogoServer
        },
        {
          text: "Find the problems in your applications",
          subtext: 'Advanced metrics',
          icon: TargetArrowIcon
        },
        {
          text: "Ease your development",
          subtext: 'No need to develop your own tools',
          icon: CodeIcon
        },
      ],
      elevation: 1,
    },
    {
      planIcon: BriefcaseIcon,
      planName: "Professional",
      planSubtitle: "Production is the place to be",
      active: plan?.value?.type === "AXONIQ_CONSOLE_PRO",
      pricing: "Starting at 40$/month",
      connections: "5 - 100",
      cloudConnection: "Required",
      metricRetention: "30 days",
      buttonUrl: "?plan-dialog=professional",
      buttonText: plan?.value ? 'Change' : 'Subscribe',
      buttonColor: "primary",
      buttonDisabled: false,
      buttonHidden: isEnterprise.value,
      advantages: [
        {
          text: "High availability through Clustering",
          subtext: "Three Axon Server nodes keep your data safe",
          icon: TopologyRing2Icon
        },
        {
          text: 'Unlock multi-context',
          subtext: 'Divide your domain into logical contexts',
          icon: DatabasePlusIcon,
        },
        {
          text: "Send alerts to your existing tools",
          subtext: "Send alerts to Slack, PagerDuty, Webhook, and E-mail",
          icon: AlarmIcon
        },
        {
          text: "Automatic scaling and balancing for processors",
          subtext: "Distribute your load over application instances",
          icon: GraphIcon
        }
      ],
      elevation: 3,
      parentPlan: 'Developer',
      parentPlanAbilities: 'Easier development, advanced metrics, and Axon Server',
    },
    {
      planIcon: BuildingSkyscraperIcon,
      planName: "Enterprise",
      planSubtitle: "Big ideas need the best support",
      pricing: "Custom",
      active: isEnterprise.value,
      connections: "Unlimited",
      cloudConnection: "Optional",
      metricRetention: "Custom",
      buttonUrl: "?plan-dialog=enterprise",
      buttonText: "Contact us",
      buttonColor: "primary",
      buttonDisabled: false,
      buttonHidden: isEnterprise.value,
      advantages: [
        {
          text: "24/7 Support",
          subtext: "Get help whenever you need it",
          icon: PhoneCallIcon,
        },
        {
          text: "Any cluster size possible",
          subtext: "We can scale to your needs",
          icon: TopologyStar3Icon
        },
        {
          text: "Multi-tier storage",
          subtext: "Move data to balance cost and performance",
          icon: Server2Icon
        },
        {
          text: "Location-aware routing",
          subtext: "Route your messages to the closest Axon Server",
          icon: WorldIcon
        },
        {
          text: "Advanced authentication",
          subtext: "LDAP, Oauth, and more",
          icon: KeyIcon
        },
      ],
      elevation: 1,
      parentPlan: 'Professional',
      parentPlanAbilities: 'High availability, alerts, and scaling'
    }
  ]
})
</script>
<template>
  <div class="px-2">
    <SubscriptionDialogs v-if="!hideCtas"/>
    <SubscriptionChangeMessages  v-if="!hideCtas"/>
    <v-row>
      <v-col v-for="plan in plans" class="d-flex flex-column">
        <SubscriptionOption :hide-cta="hideCtas" v-bind="plan"/>
      </v-col>
    </v-row>
  </div>
</template>


